import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../api';

interface EligibilityResult {
    isEligible: boolean;
    category: string;
    confidence: number;
    reasoning: string;
}

interface BulkItem {
    id: string;
    productName: string;
    price: number;
    orderDate: string;
    status: 'pending' | 'processing' | 'done' | 'error';
    result?: EligibilityResult;
    selected: boolean;
}

interface ImportItem {
    productName: string;
    price: number;
    orderDate: string;
    category: string;
    reasoning: string;
}

type TabType = 'single' | 'bulk';
type FilterType = 'all' | 'eligible' | 'ineligible';

// Session storage keys
const STORAGE_KEY = 'hsa_bulk_eligibility_results';
const IMPORT_KEY = 'hsa_pending_imports';

export default function Eligibility() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TabType>('single');
    const [query, setQuery] = useState('');
    const [result, setResult] = useState<EligibilityResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Bulk upload state
    const [bulkItems, setBulkItems] = useState<BulkItem[]>([]);
    const [processing, setProcessing] = useState(false);
    const [processedCount, setProcessedCount] = useState(0);
    const [filter, setFilter] = useState<FilterType>('all');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const api = useApi();

    // Load saved results from session storage on mount
    useEffect(() => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setBulkItems(parsed);
                // If there are processed items, switch to bulk tab
                if (parsed.some((item: BulkItem) => item.status === 'done')) {
                    setActiveTab('bulk');
                }
            } catch (e) {
                console.error('Failed to parse saved results:', e);
            }
        }
    }, []);

    // Save results to session storage whenever they change
    useEffect(() => {
        if (bulkItems.length > 0) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bulkItems));
        }
    }, [bulkItems]);

    async function handleSearch(e: React.FormEvent) {
        e.preventDefault();
        if (!query.trim()) return;

        setLoading(true);
        setError('');
        setResult(null);

        try {
            const response = await api.checkEligibility(query.trim());
            if (response.success && response.data) {
                setResult(response.data as EligibilityResult);
            } else {
                setError(response.message || 'Unable to check eligibility. Please try again.');
            }
        } catch (err) {
            console.error('Eligibility check failed:', err);
            setError('Unable to check eligibility right now. Please try again later.');
        } finally {
            setLoading(false);
        }
    }

    // Parse CSV file
    const parseCSV = useCallback((text: string): BulkItem[] => {
        const lines = text.split('\n');
        if (lines.length < 2) return [];

        // Parse header to find column indices
        const header = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
        const productNameIdx = header.findIndex(h => h.includes('product name') || h === 'product');
        const priceIdx = header.findIndex(h => h.includes('unit price') || h.includes('price') || h === 'amount');
        const dateIdx = header.findIndex(h => h.includes('order date') || h === 'date');
        const statusIdx = header.findIndex(h => h.includes('order status') || h === 'status');

        if (productNameIdx === -1) {
            throw new Error('Could not find "Product Name" column in CSV');
        }

        const items: BulkItem[] = [];
        const seenProducts = new Set<string>();

        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            // Parse CSV line (handling quoted fields)
            const fields: string[] = [];
            let field = '';
            let inQuotes = false;

            for (const char of line) {
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                    fields.push(field.trim());
                    field = '';
                } else {
                    field += char;
                }
            }
            fields.push(field.trim());

            const productName = fields[productNameIdx]?.replace(/"/g, '').trim();
            if (!productName || productName === 'Not Available') continue;

            // Skip cancelled orders
            if (statusIdx !== -1) {
                const orderStatus = fields[statusIdx]?.replace(/"/g, '').trim().toLowerCase();
                if (orderStatus === 'cancelled') continue;
            }

            // Deduplicate by product name
            if (seenProducts.has(productName.toLowerCase())) continue;
            seenProducts.add(productName.toLowerCase());

            const price = priceIdx !== -1 ? parseFloat(fields[priceIdx]?.replace(/[^0-9.-]/g, '') || '0') : 0;
            const orderDate = dateIdx !== -1 ? fields[dateIdx]?.replace(/"/g, '').split('T')[0] : '';

            items.push({
                id: `item-${i}`,
                productName,
                price,
                orderDate,
                status: 'pending',
                selected: false
            });
        }

        return items;
    }, []);

    // Handle file upload
    const handleFileUpload = useCallback(async (file: File) => {
        setError('');

        if (!file.name.endsWith('.csv')) {
            setError('Please upload a CSV file');
            return;
        }

        try {
            const text = await file.text();
            const items = parseCSV(text);

            if (items.length === 0) {
                setError('No valid product entries found in the CSV file');
                return;
            }

            setBulkItems(items);
            setProcessedCount(0);
        } catch (err) {
            console.error('CSV parsing error:', err);
            setError(err instanceof Error ? err.message : 'Failed to parse CSV file');
        }
    }, [parseCSV]);

    // Drag and drop handlers
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file) handleFileUpload(file);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFileUpload(file);
    };

    // Process items for eligibility
    const processItems = async () => {
        setProcessing(true);
        setProcessedCount(0);

        const pendingItems = bulkItems.filter(item => item.status === 'pending');
        const batchSize = 5; // Process 5 at a time for better UX

        for (let i = 0; i < pendingItems.length; i += batchSize) {
            const batch = pendingItems.slice(i, i + batchSize);

            await Promise.all(batch.map(async (item) => {
                try {
                    // Update status to processing
                    setBulkItems(prev => prev.map(p =>
                        p.id === item.id ? { ...p, status: 'processing' as const } : p
                    ));

                    const response = await api.checkEligibility(item.productName);

                    if (response.success && response.data) {
                        setBulkItems(prev => prev.map(p =>
                            p.id === item.id
                                ? { ...p, status: 'done' as const, result: response.data as EligibilityResult }
                                : p
                        ));
                    } else {
                        setBulkItems(prev => prev.map(p =>
                            p.id === item.id ? { ...p, status: 'error' as const } : p
                        ));
                    }
                } catch {
                    setBulkItems(prev => prev.map(p =>
                        p.id === item.id ? { ...p, status: 'error' as const } : p
                    ));
                }
            }));

            setProcessedCount(Math.min(i + batchSize, pendingItems.length));

            // Small delay between batches to avoid rate limiting
            if (i + batchSize < pendingItems.length) {
                await new Promise(resolve => setTimeout(resolve, 200));
            }
        }

        setProcessing(false);
    };

    // Toggle item selection
    const toggleItemSelection = (id: string) => {
        setBulkItems(prev => prev.map(item =>
            item.id === id ? { ...item, selected: !item.selected } : item
        ));
    };

    // Select all eligible items
    const selectAllEligible = () => {
        setBulkItems(prev => prev.map(item => ({
            ...item,
            selected: item.result?.isEligible === true
        })));
    };

    // Clear selection
    const clearSelection = () => {
        setBulkItems(prev => prev.map(item => ({ ...item, selected: false })));
    };

    // Clear all results
    const clearResults = () => {
        setBulkItems([]);
        sessionStorage.removeItem(STORAGE_KEY);
    };

    // Handle import selected items
    const handleImportSelected = () => {
        const selectedItems = bulkItems.filter(item => item.selected && item.result?.isEligible);

        if (selectedItems.length === 0) {
            setError('No eligible items selected to import');
            return;
        }

        // Prepare items for import review
        const importItems: ImportItem[] = selectedItems.map(item => ({
            productName: item.productName,
            price: item.price,
            orderDate: item.orderDate,
            category: item.result?.category || 'medical',
            reasoning: item.result?.reasoning || ''
        }));

        // Save to session storage for the import review page
        sessionStorage.setItem(IMPORT_KEY, JSON.stringify(importItems));

        // Navigate to upload page with import mode
        navigate('/upload?mode=import');
    };

    // Filter items
    const filteredItems = bulkItems.filter(item => {
        if (filter === 'all') return true;
        if (filter === 'eligible') return item.result?.isEligible === true;
        if (filter === 'ineligible') return item.result?.isEligible === false;
        return true;
    });

    const selectedCount = bulkItems.filter(item => item.selected).length;
    const eligibleCount = bulkItems.filter(item => item.result?.isEligible).length;
    const totalProcessed = bulkItems.filter(item => item.status === 'done').length;

    return (
        <div className="eligibility-page">
            <div className="eligibility-header">
                <h1>Is it HSA Eligible?</h1>
                <p>Search for products or check bulk purchases from your order history.</p>
            </div>

            {/* Tabs */}
            <div className="eligibility-tabs">
                <button
                    className={activeTab === 'single' ? 'active' : ''}
                    onClick={() => setActiveTab('single')}
                >
                    🔍 Single Item
                </button>
                <button
                    className={activeTab === 'bulk' ? 'active' : ''}
                    onClick={() => setActiveTab('bulk')}
                >
                    📄 Bulk Upload {totalProcessed > 0 && `(${eligibleCount} eligible)`}
                </button>
            </div>

            {/* Single Item Search Tab */}
            {activeTab === 'single' && (
                <>
                    <form onSubmit={handleSearch} className="eligibility-search">
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="e.g. Sunscreen SPF 50, Gym membership, Massage..."
                            aria-label="Search for HSA eligible items"
                        />
                        <button
                            type="submit"
                            className="check-btn"
                            disabled={loading || !query.trim()}
                        >
                            {loading ? '...' : 'Check'}
                        </button>
                    </form>

                    {error && (
                        <div className="error-message">
                            ⚠️ {error}
                        </div>
                    )}

                    {result && (
                        <div className={`eligibility-result ${result.isEligible ? 'eligible' : 'ineligible'}`}>
                            <div className="result-header">
                                <div className="result-icon">
                                    {result.isEligible ? '✓' : '✗'}
                                </div>
                                <div className="result-content">
                                    <div className="result-title-row">
                                        <h3 className="result-title">
                                            {result.isEligible ? 'Eligible' : 'Likely Not Eligible'}
                                        </h3>
                                        <span className="confidence-badge">
                                            Confidence: {result.confidence}%
                                        </span>
                                    </div>
                                    <p className="result-reasoning">{result.reasoning}</p>
                                    <div className="result-category">
                                        <span>Category:</span>
                                        <span className="category-value">{result.category || 'Unknown'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Bulk Upload Tab */}
            {activeTab === 'bulk' && (
                <div className="bulk-upload-section">
                    {bulkItems.length === 0 ? (
                        <>
                            <div
                                className={`file-drop-zone ${isDragging ? 'dragging' : ''}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <div className="drop-icon">📤</div>
                                <div className="drop-text">Drop your CSV file here, or click to browse</div>
                                <div className="drop-subtitle">
                                    Supports Amazon order history exports (.csv)
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileInputChange}
                                />
                            </div>

                            {error && (
                                <div className="error-message" style={{ marginTop: '1rem' }}>
                                    ⚠️ {error}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Progress Bar */}
                            {processing && (
                                <div className="processing-progress">
                                    <div className="progress-bar">
                                        <div
                                            className="progress-fill"
                                            style={{ width: `${(processedCount / bulkItems.filter(i => i.status === 'pending' || i.status === 'processing' || i.status === 'done').length) * 100}%` }}
                                        />
                                    </div>
                                    <div className="progress-text">
                                        <span>Processing items...</span>
                                        <span>{processedCount} / {bulkItems.length}</span>
                                    </div>
                                </div>
                            )}

                            {/* Action buttons */}
                            {!processing && totalProcessed === 0 && (
                                <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.75rem' }}>
                                    <button className="btn btn-primary" onClick={processItems}>
                                        🔍 Check Eligibility for {bulkItems.length} Items
                                    </button>
                                    <button className="btn btn-secondary" onClick={clearResults}>
                                        Clear
                                    </button>
                                </div>
                            )}

                            {/* Results */}
                            {totalProcessed > 0 && (
                                <div className="eligibility-results">
                                    <div className="results-header">
                                        <h3>
                                            Found {eligibleCount} eligible items out of {totalProcessed} checked
                                        </h3>
                                        <div className="results-filters">
                                            <button
                                                className={filter === 'all' ? 'active' : ''}
                                                onClick={() => setFilter('all')}
                                            >
                                                All ({totalProcessed})
                                            </button>
                                            <button
                                                className={filter === 'eligible' ? 'active' : ''}
                                                onClick={() => setFilter('eligible')}
                                            >
                                                ✓ Eligible ({eligibleCount})
                                            </button>
                                            <button
                                                className={filter === 'ineligible' ? 'active' : ''}
                                                onClick={() => setFilter('ineligible')}
                                            >
                                                ✗ Not Eligible ({totalProcessed - eligibleCount})
                                            </button>
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem' }}>
                                        <button className="btn btn-secondary btn-sm" onClick={selectAllEligible}>
                                            Select All Eligible
                                        </button>
                                        <button className="btn btn-secondary btn-sm" onClick={clearSelection}>
                                            Clear Selection
                                        </button>
                                        <button className="btn btn-secondary btn-sm" onClick={clearResults}>
                                            Start Over
                                        </button>
                                    </div>

                                    {error && (
                                        <div className="error-message" style={{ marginBottom: '1rem' }}>
                                            ⚠️ {error}
                                        </div>
                                    )}

                                    <div className="expenses-table-wrapper">
                                        <table className="eligibility-results-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '40px' }}>
                                                        <input
                                                            type="checkbox"
                                                            className="select-checkbox"
                                                            checked={selectedCount === eligibleCount && eligibleCount > 0}
                                                            onChange={(e) => e.target.checked ? selectAllEligible() : clearSelection()}
                                                        />
                                                    </th>
                                                    <th>Product</th>
                                                    <th>Price</th>
                                                    <th>Date</th>
                                                    <th>Status</th>
                                                    <th>Category</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredItems.map(item => (
                                                    <tr key={item.id}>
                                                        <td>
                                                            {item.result?.isEligible && (
                                                                <input
                                                                    type="checkbox"
                                                                    className="select-checkbox"
                                                                    checked={item.selected}
                                                                    onChange={() => toggleItemSelection(item.id)}
                                                                />
                                                            )}
                                                        </td>
                                                        <td style={{ maxWidth: '350px' }}>
                                                            <div style={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {item.productName}
                                                            </div>
                                                        </td>
                                                        <td>${item.price.toFixed(2)}</td>
                                                        <td>{item.orderDate || '-'}</td>
                                                        <td>
                                                            {item.status === 'pending' && (
                                                                <span className="eligibility-badge uncertain">Pending</span>
                                                            )}
                                                            {item.status === 'processing' && (
                                                                <span className="eligibility-badge uncertain">⏳ Checking...</span>
                                                            )}
                                                            {item.status === 'done' && item.result && (
                                                                <span className={`eligibility-badge ${item.result.isEligible ? 'eligible' : 'ineligible'}`}>
                                                                    {item.result.isEligible ? '✓ Eligible' : '✗ Not Eligible'}
                                                                </span>
                                                            )}
                                                            {item.status === 'error' && (
                                                                <span className="eligibility-badge ineligible">Error</span>
                                                            )}
                                                        </td>
                                                        <td>{item.result?.category || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Import Action Bar */}
                                    {selectedCount > 0 && (
                                        <div className="import-action-bar">
                                            <span className="selected-count">
                                                <strong>{selectedCount}</strong> item{selectedCount !== 1 ? 's' : ''} selected for import
                                            </span>
                                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                                <button className="btn btn-secondary" onClick={clearSelection}>
                                                    Clear Selection
                                                </button>
                                                <button className="btn btn-primary" onClick={handleImportSelected}>
                                                    📥 Review & Import Selected
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Show items before processing */}
                            {totalProcessed === 0 && !processing && (
                                <div className="eligibility-results">
                                    <div className="results-header">
                                        <h3>{bulkItems.length} items ready to check</h3>
                                    </div>
                                    <div className="expenses-table-wrapper" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <table className="eligibility-results-table">
                                            <thead>
                                                <tr>
                                                    <th>Product</th>
                                                    <th>Price</th>
                                                    <th>Order Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bulkItems.slice(0, 50).map(item => (
                                                    <tr key={item.id}>
                                                        <td style={{ maxWidth: '400px' }}>
                                                            <div style={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {item.productName}
                                                            </div>
                                                        </td>
                                                        <td>${item.price.toFixed(2)}</td>
                                                        <td>{item.orderDate}</td>
                                                    </tr>
                                                ))}
                                                {bulkItems.length > 50 && (
                                                    <tr>
                                                        <td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                                                            ... and {bulkItems.length - 50} more items
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            <div className="eligibility-info">
                <span className="info-icon">ℹ️</span>
                <div>
                    <h4>Powered by IRS Publication 502</h4>
                    <p>
                        Determinations are based on standard IRS guidance. Specific medical conditions
                        (with a Letter of Medical Necessity) may make some ineligible items eligible.
                        Always consult a tax professional for complex situations.
                    </p>
                </div>
            </div>
        </div>
    );
}
